// Schedule timezone helpers.
// Shows are stored in ET (America/New_York). Visitors see times in their
// own local timezone via luxon DateTime.

import { DateTime } from "luxon";

export const SHOW_TZ = "America/New_York";
export const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// luxon uses weekday 1-7 (Mon-Sun). We use 0-6 (Sun-Sat). Convert helpers:
const dowLuxon = (dow) => (dow === 0 ? 7 : dow); // Sun -> 7, Mon -> 1, ...

// Given a show's (day_of_week, HH:MM) in ET, return the DateTime for the
// occurrence in the same ISO week as `reference` (defaults to now).
export function showStartInWeek(show, reference = DateTime.now()) {
  const refEt = reference.setZone(SHOW_TZ);
  const [h, m] = show.start_time.split(":").map(Number);
  // ISO weekday of the show:
  const targetIsoWeekday = dowLuxon(show.day_of_week);
  return refEt
    .set({ weekday: targetIsoWeekday, hour: h, minute: m, second: 0, millisecond: 0 });
}

export function showEndInWeek(show, reference = DateTime.now()) {
  const refEt = reference.setZone(SHOW_TZ);
  const [h, m] = show.end_time.split(":").map(Number);
  const [sh, sm] = show.start_time.split(":").map(Number);
  const startDow = dowLuxon(show.day_of_week);
  let end = refEt.set({ weekday: startDow, hour: h, minute: m, second: 0, millisecond: 0 });
  const start = refEt.set({ weekday: startDow, hour: sh, minute: sm, second: 0, millisecond: 0 });
  // Handle overnight shows (end <= start means it crosses midnight)
  if (end <= start) {
    end = end.plus({ days: 1 });
  }
  return end;
}

// Is the given show currently on-air?
export function isOnAir(show, now = DateTime.now()) {
  const start = showStartInWeek(show, now);
  const end = showEndInWeek(show, now);
  const inCurrent = now >= start && now < end;
  if (inCurrent) return true;
  // Handle overnight-from-previous-week edge: check last week's occurrence
  const prevStart = start.minus({ weeks: 1 });
  const prevEnd = end.minus({ weeks: 1 });
  return now >= prevStart && now < prevEnd;
}

// Return the show that's currently on-air (or null)
export function findCurrentShow(shows, now = DateTime.now()) {
  for (const s of shows) if (isOnAir(s, now)) return s;
  return null;
}

// Find the show that starts next after `now` (within the coming 7 days)
export function findNextShow(shows, now = DateTime.now()) {
  let best = null;
  let bestStart = null;
  for (const s of shows) {
    let start = showStartInWeek(s, now);
    if (start < now) start = start.plus({ weeks: 1 });
    if (!bestStart || start < bestStart) {
      best = s;
      bestStart = start;
    }
  }
  return best ? { show: best, startsAt: bestStart } : null;
}

// Format a show's start/end range in the visitor's local timezone
export function formatShowLocalTime(show) {
  const start = showStartInWeek(show);
  const end = showEndInWeek(show);
  const startLocal = start.setZone(DateTime.local().zoneName);
  const endLocal = end.setZone(DateTime.local().zoneName);
  const sameDay = startLocal.hasSame(endLocal, "day");
  const fmt = { hour: "numeric", minute: "2-digit" };
  return `${startLocal.toLocaleString(fmt)} – ${endLocal.toLocaleString(fmt)}${sameDay ? "" : " +1"}`;
}

// Get the visitor-local weekday (0-6, Sun-Sat) for a given show
export function localWeekdayForShow(show, reference = DateTime.now()) {
  const start = showStartInWeek(show, reference);
  const local = start.setZone(DateTime.local().zoneName);
  // luxon local.weekday 1=Mon..7=Sun; convert to 0=Sun..6=Sat
  return local.weekday === 7 ? 0 : local.weekday;
}

// Group shows by the visitor's local weekday (0-6) and sort within each day
export function groupByLocalWeekday(shows) {
  const groups = Array.from({ length: 7 }, () => []);
  for (const s of shows) {
    const dow = localWeekdayForShow(s);
    groups[dow].push(s);
  }
  for (const list of groups) {
    list.sort((a, b) => {
      const sa = showStartInWeek(a).setZone(DateTime.local().zoneName);
      const sb = showStartInWeek(b).setZone(DateTime.local().zoneName);
      return sa.toMillis() - sb.toMillis();
    });
  }
  return groups;
}

export function todayLocalWeekday() {
  const d = DateTime.local();
  return d.weekday === 7 ? 0 : d.weekday;
}

export function localTimezoneLabel() {
  return DateTime.local().offsetNameShort || DateTime.local().zoneName;
}
